import { http } from '@/api/http';
import type { PageEnvelope } from '@/api/shared';

import type { FilterRequestBody } from './filterRequest';

/** Backend job name for bulk questionnaire sends — used to filter the Bulk Send tab's job tables. */
export const BULK_SEND_JOB_NAME = 'bulkSendQuestionnaireByEmailJob';

/**
 * Raw transport for the bulk-send feature (PTV2-1162): preview the recipient split, launch the async
 * job, and read a run's summary. Hand-written snake_case DTOs; the api layer maps to camelCase view
 * models. Recipients are expressed as a `query` (an indexing `FilterRequest`); the backend resolves
 * and partitions it. Self-contained (not spread into the shared apiClient), like the admin-email slice.
 */

/** Body for preview + launch. `query` is the compiled patient FilterRequest. */
export interface BulkSendRequestDto {
  readonly template_id: string;
  readonly query: FilterRequestBody;
  readonly expires_at?: string | null;
  /** Opaque JSON of the raw wizard filter, persisted for display on the job snapshot. */
  readonly filter_criteria?: string | null;
}

export interface ExcludedRecipientDto {
  readonly person_guid?: string;
  readonly reason?: string;
}

export interface ExclusionSummaryDto {
  readonly sendable?: number;
  readonly excluded?: number;
  readonly by_reason?: Record<string, number>;
  readonly excluded_patients?: readonly ExcludedRecipientDto[];
}

/** Mirrors the platform JobExecution shape (jobId polled via the jobs API). */
export interface BulkSendJobExecutionDto {
  readonly jobId?: string;
  readonly jobName?: string;
  readonly status?: string;
  readonly startTime?: string;
  readonly endTime?: string;
}

export interface BulkSendItemDto {
  readonly entity_type?: string;
  readonly entity_guid?: string;
  readonly recipient?: string | null;
  readonly status?: string;
  readonly error_code?: string | null;
  readonly error_message?: string | null;
}

export interface BulkSendSummaryDto {
  readonly job_execution_id?: number;
  readonly total?: number;
  readonly counts?: Record<string, number>;
  readonly items?: readonly BulkSendItemDto[];
}

/** One row of the Bulk Send jobs table: request snapshot + live Batch status/timing. */
export interface BulkSendJobDto {
  readonly job_execution_id?: number;
  readonly job_name?: string;
  readonly send_method?: string;
  readonly template_id?: string;
  readonly template_name?: string | null;
  readonly created_by?: string | null;
  readonly expires_at?: string | null;
  readonly filter_criteria?: string | null;
  readonly created_on?: string | null;
  readonly status?: string;
  readonly start_time?: string | null;
  readonly end_time?: string | null;
  readonly counts?: Readonly<Record<string, number>>;
}

export type BulkSendJobPageDto = PageEnvelope<BulkSendJobDto>;

/** Filters for the bulk-send jobs list; all optional (combine with AND server-side). */
export interface BulkSendJobsParams {
  readonly page?: number;
  readonly size?: number;
  readonly jobId?: string;
  readonly createdBy?: string;
  readonly templateId?: string;
  readonly status?: string;
  readonly signal?: AbortSignal;
}

export const bulkSendEndpoints = {
  /** POST /questionnaires/bulk-send/preview — partition recipients (no side effects). */
  preview: (body: BulkSendRequestDto, options?: { signal?: AbortSignal }): Promise<ExclusionSummaryDto> =>
    http.post<ExclusionSummaryDto>('/questionnaires/bulk-send/preview', body, options),

  /** POST /questionnaires/bulk-send — launch the async job; returns its execution. */
  launch: (body: BulkSendRequestDto, options?: { signal?: AbortSignal }): Promise<BulkSendJobExecutionDto> =>
    http.post<BulkSendJobExecutionDto>('/questionnaires/bulk-send', body, options),

  /** GET /questionnaires/bulk-send/{jobExecutionId}/summary — per-status counts + problem items. */
  getSummary: (jobExecutionId: string, options?: { signal?: AbortSignal }): Promise<BulkSendSummaryDto> =>
    http.get<BulkSendSummaryDto>(
      `/questionnaires/bulk-send/${encodeURIComponent(jobExecutionId)}/summary`,
      options,
    ),

  /** GET /questionnaires/bulk-send/jobs — server-side filtered + paginated bulk-send jobs. */
  listJobs: (params?: BulkSendJobsParams): Promise<BulkSendJobPageDto> => {
    const search = new URLSearchParams();
    if (params?.page !== undefined) search.set('page', String(params.page));
    if (params?.size !== undefined) search.set('size', String(params.size));
    if (params?.jobId !== undefined && params.jobId.length > 0) search.set('job_id', params.jobId);
    if (params?.createdBy !== undefined && params.createdBy.length > 0) search.set('created_by', params.createdBy);
    if (params?.templateId !== undefined && params.templateId.length > 0) search.set('template_id', params.templateId);
    if (params?.status !== undefined && params.status.length > 0) search.set('status', params.status);
    const query = search.toString();
    const path =
      query.length > 0 ? `/questionnaires/bulk-send/jobs?${query}` : '/questionnaires/bulk-send/jobs';
    return http.get<BulkSendJobPageDto>(path, params?.signal !== undefined ? { signal: params.signal } : undefined);
  },
};
